import { z } from 'zod'

// === RESULT TYPE ===
// Usato al posto di throw per errori attesi

export type ValidationResult<T> =
  | { ok: true; data: T }
  | { ok: false; errors: ValidationError[] }

export type ValidationError = {
  field: string
  message: string
}

// === VALIDAZIONE GENERICA ===

/** Valida dati con uno schema Zod, ritorna Result */
export function validate<T>(schema: z.ZodSchema<T>, data: unknown): ValidationResult<T> {
  const result = schema.safeParse(data)

  if (result.success) {
    return { ok: true, data: result.data }
  }

  const errors: ValidationError[] = result.error.issues.map(issue => ({
    field: issue.path.join('.'),
    message: issue.message,
  }))

  return { ok: false, errors }
}

/** Valida e lancia errore se fallisce (usa solo dove puoi gestire eccezioni) */
export function validateOrThrow<T>(schema: z.ZodSchema<T>, data: unknown): T {
  return schema.parse(data)
}

// === VALIDATORI SPECIFICI ===

/** Codice fiscale italiano (16 caratteri alfanumerici) */
export function isValidCF(cf: string | null | undefined): boolean {
  if (!cf) return false
  const cleaned = cf.trim().toUpperCase()
  return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(cleaned)
}

/** Partita IVA italiana (11 cifre) */
export function isValidPIVA(piva: string | null | undefined): boolean {
  if (!piva) return false
  const cleaned = piva.trim()
  return /^\d{11}$/.test(cleaned)
}

/** CAP italiano (5 cifre) */
export function isValidCAP(cap: string | null | undefined): boolean {
  if (!cap) return false
  return /^\d{5}$/.test(cap.trim())
}

/** Data in formato ISO (YYYY-MM-DD) */
export function isValidDateISO(date: string | null | undefined): boolean {
  if (!date) return false
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && !isNaN(Date.parse(date))
}

/** Provincia italiana (2 lettere) */
export function isValidProvincia(prov: string | null | undefined): boolean {
  if (!prov) return false
  return /^[A-Z]{2}$/i.test(prov.trim())
}

// === ZOD CUSTOM SCHEMAS ===
// Per usare nei form o API

export const CFSchema = z.string().refine(isValidCF, { message: 'Codice fiscale non valido' })
export const PIVASchema = z.string().refine(isValidPIVA, { message: 'Partita IVA non valida' })
export const CAPSchema = z.string().refine(isValidCAP, { message: 'CAP non valido' })
export const DateISOSchema = z.string().refine(isValidDateISO, { message: 'Data non valida (YYYY-MM-DD)' })
export const ProvinciaSchema = z.string().refine(isValidProvincia, { message: 'Provincia non valida' })

// === HELPER ===

/** Pulisce stringa: trim e null se vuota */
export function cleanString(s: string | null | undefined): string | null {
  if (!s) return null
  const trimmed = s.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Normalizza CF: uppercase e trim */
export function normalizeCF(cf: string | null | undefined): string | null {
  if (!cf) return null
  return cf.trim().toUpperCase()
}

/** Normalizza nome: trim e title case */
export function normalizeNome(nome: string | null | undefined): string | null {
  if (!nome) return null
  const trimmed = nome.trim()
  if (!trimmed) return null
  // Title case semplice
  return trimmed
    .toLowerCase()
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
